/**
 * A new table with its columns, in one drawer.
 *
 * A table is created WITH its columns, not created empty and then filled in
 * one "Add column" at a time. Two columns almost every table wants are
 * included by default and switched off rather than typed; "Add column"
 * appends as many as the table needs. It all becomes one CREATE TABLE.
 *
 * The statement is one click away under the form rather than always on
 * screen: the form is the question, and the SQL is there to be read — and
 * copied into a migration — when someone wants it.
 */
import { useEffect, useState } from 'react';
import ToggleSwitchCheckbox from '../../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox';
import { DsButton, Eyebrow } from '../../components/ds';
import { PlusIcon } from '../../components/ds/icons';
import { quoteIdent } from './cells';
import ColumnCard, { ColumnSpec, columnDefinition, NAME_HINT, newColumnSpec } from './ColumnFields';
import SideDrawer, { DrawerPanel, Field, inputClass, touchSize } from './SideDrawer';
import { StatementDisclosure } from './StatementPreview';
import { useStatement } from './useStatement';

const CreateTableDrawer = ({ appId, open, initialName, onClose, onCreated }: {
  appId: string,
  open: boolean,
  /** What the explorer's search held, when it offered "Create table <name>". */
  initialName?: string,
  onClose: () => void,
  /** The table exists; its id (`public.name`) so the screen can open it. */
  onCreated: (id: string) => void,
}) => {
  const [name, setName] = useState('');
  const [withId, setWithId] = useState(true);
  const [withCreatedAt, setWithCreatedAt] = useState(true);
  const [columns, setColumns] = useState<ColumnSpec[]>(() => [newColumnSpec()]);
  const { run, isRunning, failure, reset } = useStatement(appId);

  useEffect(() => {
    if (open) {
      setName((initialName || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, ''));
      setWithId(true);
      setWithCreatedAt(true);
      setColumns([newColumnSpec()]);
      reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key: string, patch: Partial<ColumnSpec>) =>
    setColumns(current => current.map(column => (column.key === key ? { ...column, ...patch } : column)));

  const lines = [
    withId ? 'id bigserial PRIMARY KEY' : '',
    // A card left without a name is a card nobody filled in, not an error.
    ...columns.map(columnDefinition),
    withCreatedAt ? 'created_at timestamptz NOT NULL DEFAULT now()' : '',
  ].filter(Boolean);

  const tableName = name.trim();
  const sql = tableName && lines.length
    ? `CREATE TABLE public.${quoteIdent(tableName)} (\n  ${lines.join(',\n  ')}\n);`
    : '';

  const columnCount = (withId ? 1 : 0) + (withCreatedAt ? 1 : 0) + columns.length;
  const presetCount = (withId ? 1 : 0) + (withCreatedAt ? 1 : 0);
  const size = touchSize();

  const create = () => {
    if (sql) {
      run(sql, () => onCreated(`public.${tableName}`));
    }
  };

  return (
    <SideDrawer open={open} onClose={onClose} width="md:w-[640px]">
      <DrawerPanel
        eyebrow="schema public"
        title="New table"
        blurb="Creates a table in the public schema."
        onClose={onClose}
        summary={`${columnCount} column${columnCount === 1 ? '' : 's'} · one CREATE TABLE statement.`}
        footer={
          <div className="flex items-center justify-end gap-2 flex-wrap ml-auto">
            <DsButton variant="ghost" size={size} onClick={onClose} isDisabled={isRunning}>Cancel</DsButton>
            <DsButton variant="primary" size={size} onClick={create} isDisabled={isRunning || !sql}>
              {isRunning ? 'Creating…' : 'Create table'}
            </DsButton>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <Field label="Name" isRequired hint={NAME_HINT}>
            <input
              autoFocus
              value={name}
              onChange={event => setName(event.target.value)}
              onKeyDown={event => { if (event.key === 'Enter') { create(); } }}
              placeholder="orders"
              spellCheck={false}
              className={inputClass}
            />
          </Field>

          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <Eyebrow>included by default</Eyebrow>
              <span className="font-courrier text-ds-115 text-navy-300">{presetCount} of 2 columns</span>
            </div>
            <ToggleSwitchCheckbox
              labelText="Primary key"
              description="id bigserial, numbered for every new row. Editing rows from this screen needs one."
              value={withId}
              onChange={event => setWithId(event.target.checked)}
            />
            <ToggleSwitchCheckbox
              labelText="Created at"
              description="created_at timestamptz, filled with the moment the row is inserted."
              value={withCreatedAt}
              onChange={event => setWithCreatedAt(event.target.checked)}
            />
          </div>

          {columns.map((column, index) => (
            <ColumnCard
              key={column.key}
              label={`column ${index + 1}`}
              spec={column}
              onChange={patch => update(column.key, patch)}
              onRemove={columns.length > 1
                ? () => setColumns(current => current.filter(item => item.key !== column.key))
                : undefined}
            />
          ))}

          <DsButton
            variant="subtle"
            size={size}
            className="w-full"
            onClick={() => setColumns(current => [...current, newColumnSpec()])}
          >
            <PlusIcon size={16} /> Add column
          </DsButton>

          <StatementDisclosure sql={sql} emptyText="Name the table to see the statement." />

          {failure ? (
            <p className="m-0 font-courrier text-ds-115 leading-[1.5] text-state-err">{failure}</p>
          ) : null}
        </div>
      </DrawerPanel>
    </SideDrawer>
  );
};

export default CreateTableDrawer;
